import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingBasket,
    faEllipsisH,
    faBarcode,
    faCirclePlus,
    faChartPie,
    faPlus,
    faCubes,
    faCircleCheck,
    faCircleXmark,
    faShieldHalved,
    faBell,
    faBoxesStacked,
    faFileImport,
    faPen,
    faEye,
    faTag,
    faHospital,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import CreateProductModal from '../components/Modal/CreateProductModal';
import EditProductModal from '../components/Modal/EditProductModal';
import ImportPurchaseOrderModal from '../components/Modal/ImportPurchaseOrderModal';
import { NavLink, useNavigate } from 'react-router-dom';
import { useProductActions } from '../hooks/useProductActions';
import InputText from '../components/Form/Fields/InputText';
import FabGroup from "../components/FabGroup";
import { eventBus } from "../bus/eventBus";
import Select from "../components/Form/Fields/Select";
import { getProductStatusOptions } from "../config/ProductStatus";
import InputSliderRange from '../components/Form/Fields/InputSliderRange';
import { getYesNoOptions } from '../config/YesNo';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import ProductQuickView from '../components/Product/ProductQuickView';
import {
    productIdColumn, productNameColumn, productPriceColumn, productStockColumn,
    productStatusColumn, productRefundableColumn, productBarcodeColumn,
} from '../components/Product/productColumns';
import { useAuth } from '../context/AuthProvider';
import { transliterateToArabic } from '../utils/transliterateArabic';
import { API_BASE_URL } from '../api';
import fitColumns from '../components/Listing/fitColumns';

// Blocks of the filter panel (see TableFilters' filterable.group).
const FILTER_GROUPS = {
    general: { title: "Général", order: 1 },
    priceStock: { title: "Prix & stock", order: 2 },
    classification: { title: "Classification", order: 3 },
    regulation: { title: "Réglementation", order: 4 },
};

const formatAmount = (val) =>
    new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

export default function Products() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isArabic = i18n.language === 'ar';
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCreateProductModal, setShowCreateProductModal] = useState(false);
    const [showImportPurchaseOrderModal, setShowImportPurchaseOrderModal] = useState(false);
    const [quickViewProduct, setQuickViewProduct] = useState(null);
    const [laboratoryOptions, setLaboratoryOptions] = useState([{ value: '', label: t('All Laboratories') }]);
    const [familyOptions, setFamilyOptions] = useState([{ value: '', label: 'Toutes' }]);
    // Upper end of the price filter slider — the catalog's highest price, rounded up.
    const [maxPriceBound, setMaxPriceBound] = useState(1000);
    const { setTitle, setTitleIcon } = usePageTitle();
    const { settings, hasRight } = useAuth();
    const canEdit = hasRight('PRODUCTS_EDIT');
    const dblClickAddToCart = settings['sales_inventory_product_list_dblclick_add_to_cart'] !== false;
    const quickViewEnabled = settings['general_general_settings_product_quick_view_enabled'] === true;
    const handleProductClick = (row) => {
        if (quickViewEnabled) setQuickViewProduct(row);
        else navigate(`/products/${row.id}`);
    };
    const globalStockAlertThreshold = Number(settings?.stock_inventory_min_stock_alert) || 10;
    const tableRef = useRef(null);

    const { addToCart, addingToCartId, handleAddStock, handleViewStock, openQuickEdit, modals: productModals } =
        useProductActions({ onRefresh: () => tableRef.current?.reload() });

    const [bulkAddingToCart, setBulkAddingToCart] = useState(false);

    const handleBulkAddToCart = async (ids) => {
        if (bulkAddingToCart || ids.length === 0) return;
        setBulkAddingToCart(true);
        let success = 0, failed = 0;

        for (const id of ids) {
            try {
                const { data } = await axios.get(`${API_BASE_URL}/api/v1/cart/product/${id}`, { withCredentials: true });
                eventBus.emit('newCartItem', data);
                success++;
            } catch {
                failed++;
            }
        }

        setBulkAddingToCart(false);
        if (success > 0) toast.success(`${success} produit${success > 1 ? 's' : ''} ajouté${success > 1 ? 's' : ''} au panier`);
        if (failed > 0) toast.error(`${failed} produit${failed > 1 ? 's' : ''} n'${failed > 1 ? 'ont' : 'a'} pas pu être ajouté${failed > 1 ? 's' : ''} au panier`);
    };

    useEffect(() => {
        setTitle(`${t('Products')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">Catalogue et stock</span>`);
        document.title = t('Products');
        setTitleIcon('ti-vaccine-bottle');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useEffect(() => {
        const reload = () => tableRef.current?.reload();
        eventBus.on('orderPlaced', reload);
        return () => eventBus.off('orderPlaced', reload);
    }, []);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/laboratories`, {
            params: { page: 0, size: 1000 },
            withCredentials: true,
        })
            .then(({ data }) => {
                const labs = data.content || [];
                setLaboratoryOptions([
                    { value: '', label: t('All Laboratories') },
                    ...labs.map(lab => ({ value: lab.id, label: lab.name, icon: <LaboratoryLogo id={lab.id} name={lab.name} size={20} /> })),
                ]);
            })
            .catch(() => {});
    }, [t]);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/products/families`, { withCredentials: true })
            .then(({ data }) => setFamilyOptions([
                { value: '', label: 'Toutes' },
                ...(data || []).map(family => ({ value: family, label: family })),
            ]))
            .catch(() => {});
    }, []);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/products`, {
            params: { page: 0, size: 1, sortField: 'publicPrice', sortDirection: 'desc' },
            withCredentials: true,
        })
            .then(({ data }) => {
                const top = Number(data.content?.[0]?.publicPrice);
                if (top > 0) setMaxPriceBound(Math.ceil(top / 100) * 100);
            })
            .catch(() => {});
    }, []);

    useBreadcrumbSetup([{ label: t("Products"), url: "/products" }]);

    const handleEdit = (productId) => {
        axios.get(`${API_BASE_URL}/api/v1/products/${productId}`, { withCredentials: true })
            .then(response => {
                setSelectedProduct(response.data);
                setShowModal(true);
            })
            .catch(error => console.error("Failed to fetch product details", error));
    };

    // Card-view design for this listing (Table's enableCardView feature) — mirrors the
    // table columns' key info (status, name, lab, price, stock, refundable) in a compact
    // grid tile instead of a row.
    const renderProductCard = (row) => {
        const qty = row.totalStock;
        const marketed = row.status === "MARKETED";
        const stockColor = qty > 3 ? "#059669" : qty > 0 ? "#d97706" : "#dc2626";
        const stockBg = qty > 3 ? "#d1fae5" : qty > 0 ? "#fef3c7" : "#fee2e2";

        return (
            <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => handleProductClick(row)}>
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: marketed ? "#10b981" : "#9ca3af" }}>
                            <FontAwesomeIcon icon={marketed ? faCircleCheck : faCircleXmark} style={{ fontSize: 14 }} />
                            {marketed ? t("Marketed") : t("Not marketed")}
                        </span>
                        {row.refundable === true && (
                            <FontAwesomeIcon
                                icon={faShieldHalved}
                                data-tooltip-id="tooltip"
                                data-tooltip-content={t("Refundable")}
                                style={{ color: "#2563eb", fontSize: 13 }}
                            />
                        )}
                    </div>

                    <NavLink
                        to={`/products/${row.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 13.5, fontWeight: 600, color: "#111827", lineHeight: 1.3 }}
                    >
                        {isArabic ? (row.nameAr || transliterateToArabic(row.name)) : row.name}
                    </NavLink>

                    {(row.presentation || row.dosage) && (
                        <div style={{ fontSize: 11.5, color: "#6b7280" }}>
                            {[row.presentation, row.dosage].filter(Boolean).join(' · ')}
                        </div>
                    )}

                    {row.laboratory?.name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: "#9ca3af" }}>
                            <LaboratoryLogo id={row.laboratory.id} name={row.laboratory.name} size={14} />
                            {row.laboratory.name}
                        </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                            {formatAmount(row.publicPrice)} <small style={{ fontSize: 10, color: "#9ca3af", fontWeight: 500 }}>dhs</small>
                        </span>
                        {(qty > 0 || row.hasStockHistory) && (
                            <span style={{
                                fontSize: 11, fontWeight: 700, color: stockColor, background: stockBg,
                                padding: '2px 8px', borderRadius: 999, textTransform: 'lowercase',
                            }}>
                                {qty} {t('product.in_stock')}
                            </span>
                        )}
                    </div>

                    <div className="d-flex gap-2" style={{ marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center justify-content-center gap-2"
                            style={{
                                fontSize: 12, flex: 1,
                                background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)',
                                border: '1px solid #bfdbfe',
                                color: '#1d4ed8',
                                fontWeight: 600,
                            }}
                            onClick={() => addToCart(row.id, row.totalStock)}
                            disabled={addingToCartId === row.id}
                        >
                            {addingToCartId === row.id
                                ? <span className="spinner-border spinner-border-sm" />
                                : <FontAwesomeIcon icon={faShoppingBasket} />}
                            {t("Add to cart")}
                        </button>
                        <div className="cell-dropdown">
                            <button
                                type="button"
                                className="btn btn-sm btn-light dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <FontAwesomeIcon icon={faEllipsisH} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">
                                <li>
                                    <button className="dropdown-item" onClick={() => addToCart(row.id, row.totalStock)}>
                                        <FontAwesomeIcon icon={faShoppingBasket} className="me-2 text-muted" />
                                        {t("Add to cart")}
                                    </button>
                                </li>
                                {canEdit && <li><hr className="dropdown-divider" /></li>}
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => handleAddStock(row.id)}>
                                            <FontAwesomeIcon icon={faPlus} className="me-2 text-muted" />
                                            {t("Add to stock")}
                                        </button>
                                    </li>
                                )}
                                <li>
                                    <button className="dropdown-item" onClick={() => handleViewStock(row.id, row.name)}>
                                        <FontAwesomeIcon icon={faCubes} className="me-2 text-muted" />
                                        {t("View batches")}
                                    </button>
                                </li>
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'minimumStock', 'Seuil d\'alerte stock', row.minimumStock > 0 ? row.minimumStock : globalStockAlertThreshold)}>
                                            <FontAwesomeIcon icon={faBell} className="me-2 text-muted" />
                                            Seuil d'alerte stock
                                        </button>
                                    </li>
                                )}
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'maximumStock', 'Seuil de stock max', row.maximumStock > 0 ? row.maximumStock : '')}>
                                            <FontAwesomeIcon icon={faBoxesStacked} className="me-2 text-muted" />
                                            Seuil de stock max
                                        </button>
                                    </li>
                                )}
                                {canEdit && <li><hr className="dropdown-divider" /></li>}
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'barcode', 'Code-barres', row.barcode)}>
                                            <FontAwesomeIcon icon={faBarcode} className="me-2 text-muted" />
                                            {t("Edit barcode")}
                                        </button>
                                    </li>
                                )}
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'ppv', 'PPV (prix public)', row.publicPrice)}>
                                            <FontAwesomeIcon icon={faTag} className="me-2 text-muted" />
                                            {t("Edit PPV price")}
                                        </button>
                                    </li>
                                )}
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'pph', 'PPH (prix hospitalier)', row.hospitalPrice)}>
                                            <FontAwesomeIcon icon={faHospital} className="me-2 text-muted" />
                                            {t("Edit PPH price")}
                                        </button>
                                    </li>
                                )}
                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <NavLink to={`/products/${row.id}`} className="dropdown-item">
                                        <FontAwesomeIcon icon={faEye} className="me-2 text-muted" />
                                        {t("View product")}
                                    </NavLink>
                                </li>
                                {canEdit && (
                                    <li>
                                        <button className="dropdown-item" onClick={() => handleEdit(row.id)}>
                                            <FontAwesomeIcon icon={faPen} className="me-2 text-muted" />
                                            Modifier le produit
                                        </button>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Data cells shared with every product list (components/Product/productColumns.js);
    // the filters and the actions menu are this screen's own.
    const baseColumns = [
        productIdColumn(),
        productNameColumn({ t, isArabic }),
        {
            ...productPriceColumn({ t, onEditPrice: canEdit ? (row) => openQuickEdit(row.id, 'ppv', 'PPV (prix public)', row.publicPrice) : undefined }),
            filterable: { group: FILTER_GROUPS.priceStock, component: InputSliderRange, label: t("Price"), min: 0, max: maxPriceBound, step: 1, unit: "dhs" },
        },
        {
            ...productStockColumn({ t, alertThreshold: globalStockAlertThreshold }),
            // stock has no reliable catalog-wide max (stockCount sums every pharmacy) — fixed scale,
            // "200+" at the end; the Max input still takes any value
            filterable: { group: FILTER_GROUPS.priceStock, component: InputSliderRange, min: 0, max: 200, step: 1 },
        },
        {
            ...productStatusColumn({ t }),
            filterable: { group: FILTER_GROUPS.general, component: Select, options: getProductStatusOptions(), label: t("Status") },
        },
        {
            accessor: "laboratoryId",
            visible: false,
            // searchable: hundreds of laboratories — typing to filter is needed here
            filterable: { group: FILTER_GROUPS.general, component: Select, options: laboratoryOptions, label: t("Laboratory"), searchable: true },
        },
        {
            ...productRefundableColumn({ t }),
            filterable: { group: FILTER_GROUPS.regulation, component: Select, options: getYesNoOptions(), label: t("Refundable") },
        },
        productBarcodeColumn({ t }),
        {
            // Not shown as its own column — the barcode text filter above already covers
            // searching by value, this just offers "with/without a barcode at all".
            Header: "Code-barres (présence)",
            accessor: "hasBarcode",
            visible: false,
            filterable: { group: FILTER_GROUPS.general,
                component: Select,
                label: "Présence code-barres",
                options: [
                    { value: "", label: "Tous" },
                    { value: "true", label: "Avec code-barres" },
                    { value: "false", label: "Sans code-barres" },
                ],
            },
        },
        // Filter-only entries below (no Header, so they stay out of the "Colonnes" menu) —
        // the matching backend keys live in ProductFilterConfig / ProductService#extraFilterSpec.
        {
            accessor: "stockAlert",
            visible: false,
            filterable: { group: FILTER_GROUPS.priceStock,
                component: Select,
                label: "Alerte de stock",
                options: [
                    { value: "", label: "Tous" },
                    { value: "OUT", label: "En rupture" },
                    { value: "LOW", label: "Sous le seuil d'alerte" },
                    { value: "OVER", label: "Au-dessus du stock max" },
                    { value: "NORMAL", label: "Stock normal" },
                ],
            },
        },
        {
            accessor: "expiry",
            visible: false,
            filterable: { group: FILTER_GROUPS.priceStock,
                component: Select,
                label: "Péremption",
                options: [
                    { value: "", label: "Tous" },
                    { value: "EXPIRED", label: "Lots périmés" },
                    { value: "30", label: "Expire sous 30 jours" },
                    { value: "60", label: "Expire sous 60 jours" },
                    { value: "90", label: "Expire sous 90 jours" },
                ],
            },
        },
        {
            accessor: "psychoactive",
            visible: false,
            filterable: { group: FILTER_GROUPS.regulation,
                component: Select,
                label: "Psychotrope",
                options: [
                    { value: "", label: "Tous" },
                    { value: "true", label: "Psychotropes uniquement" },
                    { value: "false", label: "Non psychotropes" },
                ],
            },
        },
        {
            accessor: "tableLetter",
            visible: false,
            filterable: { group: FILTER_GROUPS.regulation,
                component: Select,
                label: "Tableau",
                options: [
                    { value: "", label: "Tous" },
                    { value: "A", label: "Tableau A" },
                    { value: "B", label: "Tableau B" },
                    { value: "C", label: "Tableau C" },
                    { value: "NONE", label: "Hors tableau" },
                ],
            },
        },
        {
            accessor: "family",
            visible: false,
            // searchable: ~1000 therapeutic classes
            filterable: { group: FILTER_GROUPS.classification, component: Select, label: "Classe thérapeutique", options: familyOptions, searchable: true },
        },
        {
            accessor: "productType",
            visible: false,
            filterable: { group: FILTER_GROUPS.classification,
                component: Select,
                label: "Type",
                options: [
                    { value: "", label: "Tous" },
                    { value: "Princeps", label: "Princeps" },
                    { value: "Générique", label: "Générique" },
                ],
            },
        },
        {
            accessor: "form",
            visible: false,
            filterable: { group: FILTER_GROUPS.classification,
                component: Select,
                label: "Forme",
                // Whole word (or its plural) in the name after its comma ("…, Comprimé
                // effervescent"), accents ignored.
                options: [
                    { value: "", label: "Toutes" },
                    { value: "comprime", label: "Comprimé" },
                    { value: "gelule", label: "Gélule" },
                    { value: "capsule", label: "Capsule" },
                    { value: "sirop", label: "Sirop" },
                    { value: "solution", label: "Solution" },
                    { value: "suspension", label: "Suspension" },
                    { value: "poudre", label: "Poudre" },
                    { value: "sachet", label: "Sachet" },
                    { value: "granule", label: "Granulés" },
                    { value: "injectable", label: "Injectable" },
                    { value: "ampoule", label: "Ampoule" },
                    { value: "suppositoire", label: "Suppositoire" },
                    { value: "ovule", label: "Ovule" },
                    { value: "creme", label: "Crème" },
                    { value: "pommade", label: "Pommade" },
                    { value: "gel", label: "Gel" },
                    { value: "collyre", label: "Collyre" },
                    { value: "goutte", label: "Gouttes" },
                    { value: "spray", label: "Spray" },
                    { value: "pastille", label: "Pastille" },
                ],
            },
        },
        {
            accessor: "hasEquivalents",
            visible: false,
            filterable: { group: FILTER_GROUPS.general,
                component: Select,
                label: "Équivalents",
                options: [
                    { value: "", label: "Tous" },
                    { value: "true", label: "Avec équivalents" },
                    { value: "false", label: "Sans équivalent" },
                ],
            },
        },
        {
            accessor: "composition",
            visible: false,
            filterable: { group: FILTER_GROUPS.classification, component: InputText, label: "Composition (DCI)", placeholder: "ex. paracétamol" },
        },
        {
            Header: "",
            accessor: "actions",
            className: "col-actions",
            Cell: (row) => (
                <div className="cell-dropdown" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <button
                        className="cell-actions-btn"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Ajouter au panier"
                        onClick={() => addToCart(row.id, row.totalStock)}
                        disabled={addingToCartId === row.id}
                    >
                        {addingToCartId === row.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <FontAwesomeIcon icon={faShoppingBasket} />}
                    </button>
                    <button
                        className="cell-actions-btn"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Voir les lots"
                        onClick={() => handleViewStock(row.id, row.name)}
                    >
                        <FontAwesomeIcon icon={faCubes} />
                    </button>
                    <button
                        className="cell-actions-btn dropdown-toggle"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Plus d'actions"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => addToCart(row.id, row.totalStock)}>
                                <FontAwesomeIcon icon={faShoppingBasket} className="me-2 text-muted" />
                                {t("Add to cart")}
                            </button>
                        </li>
                        {canEdit && <li><hr className="dropdown-divider" /></li>}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => handleAddStock(row.id)}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2 text-muted" />
                                    {t("Add to stock")}
                                </button>
                            </li>
                        )}
                        <li>
                            <button className="dropdown-item" onClick={() => handleViewStock(row.id, row.name)}>
                                <FontAwesomeIcon icon={faCubes} className="me-2 text-muted" />
                                {t("View batches")}
                            </button>
                        </li>
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'minimumStock', 'Seuil d\'alerte stock', row.minimumStock > 0 ? row.minimumStock : globalStockAlertThreshold)}>
                                    <FontAwesomeIcon icon={faBell} className="me-2 text-muted" />
                                    Seuil d'alerte stock
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'maximumStock', 'Seuil de stock max', row.maximumStock > 0 ? row.maximumStock : '')}>
                                    <FontAwesomeIcon icon={faBoxesStacked} className="me-2 text-muted" />
                                    Seuil de stock max
                                </button>
                            </li>
                        )}
                        {canEdit && <li><hr className="dropdown-divider" /></li>}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'barcode', 'Code-barres', row.barcode)}>
                                    <FontAwesomeIcon icon={faBarcode} className="me-2 text-muted" />
                                    {t("Edit barcode")}
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'ppv', 'PPV (prix public)', row.publicPrice)}>
                                    <FontAwesomeIcon icon={faTag} className="me-2 text-muted" />
                                    {t("Edit PPV price")}
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'pph', 'PPH (prix hospitalier)', row.hospitalPrice)}>
                                    <FontAwesomeIcon icon={faHospital} className="me-2 text-muted" />
                                    {t("Edit PPH price")}
                                </button>
                            </li>
                        )}
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                            <NavLink to={`/products/${row.id}`} className="dropdown-item">
                                <FontAwesomeIcon icon={faEye} className="me-2 text-muted" />
                                {t("View product")}
                            </NavLink>
                        </li>
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => handleEdit(row.id)}>
                                    <FontAwesomeIcon icon={faPen} className="me-2 text-muted" />
                                    Modifier le produit
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];
    // only the product name stretches, the others fit their content
    const columns = fitColumns(baseColumns, 'name');

    const buttons = [
        ...(canEdit ? [{ label: t('New Product'), className: 'btn-primary', icon: faCirclePlus, onClick: () => setShowCreateProductModal(true) }] : []),
    ];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/products`}
                columns={columns}
                defaultSort="name"
                defaultDirection="asc"
                pageSize={20}
                extraParams={{ prefixMatch: true }}
                emptyMessage="Aucun produit"
                searchable={true}
                multiSelect={true}
                searchPlaceholder={t("products.search_placeholder")}
                voiceSearch
                onRowDoubleClick={dblClickAddToCart ? (row) => addToCart(row.id, row.totalStock) : undefined}
                onRowClick={handleProductClick}
                getRowDragData={(row) => ({ type: 'product', id: row.id, name: row.name })}
                enableCardView={true}
                renderCard={renderProductCard}
                selectionActions={[
                    {
                        label: bulkAddingToCart ? 'Ajout en cours…' : 'Ajouter au panier',
                        icon: faShoppingBasket,
                        onClick: (ids) => handleBulkAddToCart(ids),
                    },
                ]}
            />

            {quickViewProduct && (
                <ProductQuickView
                    product={quickViewProduct}
                    onClose={() => setQuickViewProduct(null)}
                    onAddToCart={addToCart}
                    addingToCart={addingToCartId === quickViewProduct.id}
                    onAddStock={handleAddStock}
                    onViewStock={handleViewStock}
                />
            )}

            <CreateProductModal
                show={showCreateProductModal}
                onClose={() => setShowCreateProductModal(false)}
                onSave={() => tableRef.current?.reload()}
            />

            <EditProductModal
                product={selectedProduct}
                show={showModal}
                onClose={() => setShowModal(false)}
                onSave={() => tableRef.current?.reload()}
            />

            <ImportPurchaseOrderModal
                show={showImportPurchaseOrderModal}
                onClose={() => setShowImportPurchaseOrderModal(false)}
                onConfirmed={() => tableRef.current?.reload()}
            />

            <FabGroup buttons={buttons} />

            {productModals}
        </>
    );
}
